import { useRef, useState, type FC } from "react";

import { enqueueSnackbar } from "notistack";

import { Button } from "@/shared/components/ui/button";
import { FileCard } from "@/shared/components/ui/file-card";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import DocumentUploadInput from "@/shared/components/document-viewer/DocumentUploadInput";
import UploadDocumentsDialog from "@/shared/components/document-viewer/UploadDocumentsDialog";
import { UploadDocumentsForm } from "@/shared/components/document-viewer/UploadDocumentsForm";
import { UploadedDocument } from "@/shared/components/document-viewer/UploadedDocument";
import { QrUploadInstructions } from "@/shared/components/QrUploadInstructions";
import ContactProfilePicture from "@/shared/components/Contact/ContactProfilePicture";
import { UploadDocumentModal } from "@/shared/components/overlay/UploadDocumentModal";
import { MobileUploadModal } from "@/shared/components/overlay/MobileUploadModal";
import { UploadLimitModal } from "@/pages/postloss/documents/components/UploadLimitModal";
import { UploadMethodCards } from "@/pages/postloss/find-assets/components/UploadMethodCards";
import {
  TriageModalDeathCertificateStep,
  type CompletedUpload,
} from "@/pages/postloss/overview/components/TriageModalDeathCertificateStep";
import { LegalAuthorityWillUploadStep } from "@/pages/postloss/overview/components/LegalAuthorityWillUploadStep";
import LawyerUploadDialog from "@/pages/lawyer/client-detail/components/UploadDialog";
import { UploadId } from "@/pages/onboarding/components/screens/UploadId";
import { TaxReturnUpload } from "@/pages/postloss/modules/financial-accounts/components/TaxReturnUpload";
import { usePerson } from "@/shared/hooks/useAppContext";
import { DocumentType } from "@/shared/types/documentTypes";

import type { FlowAnswers } from "@/shared/constants/flowTypes";

type UploadZoneStatus = "idle" | "loading" | "complete" | "error";

const UPLOAD_ZONE_STATUSES: UploadZoneStatus[] = ["idle", "loading", "complete", "error"];

const SAMPLE_DOCUMENT_TYPES = [
  { id: DocumentType.DEATH_CERTIFICATE, label: "Death certificate" },
  { id: DocumentType.WILL, label: "Will" },
];

const SAMPLE_FILE_SIZE = 1_240_000;

const logEvent = (message: string) => enqueueSnackbar(message, { variant: "default" });

export const UploadPage: FC = () => {
  const person = usePerson();
  const estateId = person?.estate_id;
  const profileInputRef = useRef<HTMLInputElement>(null);

  const [zoneFile, setZoneFile] = useState<File | null>(null);
  const [zoneStatus, setZoneStatus] = useState<UploadZoneStatus>("idle");
  const [methodFile, setMethodFile] = useState<File | null>(null);
  const [uploadedDocType, setUploadedDocType] = useState<DocumentType>(
    DocumentType.DEATH_CERTIFICATE,
  );
  const [qrCount, setQrCount] = useState(0);
  const [profileUrl, setProfileUrl] = useState<string | null>(null);
  const [completedUploads, setCompletedUploads] = useState<CompletedUpload[]>([]);
  const [flowAnswers, setFlowAnswers] = useState<FlowAnswers>({});
  const [openOverlay, setOpenOverlay] = useState<
    "upload-modal" | "documents-dialog" | "mobile" | "limit" | "lawyer" | null
  >(null);

  const closeOverlay = () => setOpenOverlay(null);
  const setFlowAnswer = (questionId: string, value: string | string[]) =>
    setFlowAnswers((prev) => ({ ...prev, [questionId]: value }));
  const flowProps = {
    answers: flowAnswers,
    setAnswer: setFlowAnswer,
    onComplete: () => logEvent("Flow step completed"),
    goToScreen: (screenId: string) => logEvent(`goToScreen: ${screenId}`),
  };
  const sampleFiles = [
    new File(["a"], "death-certificate.pdf", { type: "application/pdf" }),
    new File(["b"], "will.pdf", { type: "application/pdf" }),
    new File(["c"], "bank-statement.pdf", { type: "application/pdf" }),
  ];

  return (
    <div className="w-full px-12 py-12">
      <header className="mb-12">
        <p className="mb-1 text-sm font-medium text-brand">Design System</p>
        <h1 className="mb-3 font-sans text-2xl font-bold tracking-tight">Upload</h1>
        <p className="text-sm leading-relaxed text-secondary">
          Every upload surface in the app, wired up so you can try the interactions. Components that
          store files use your current estate, so uploads here are real.
        </p>
      </header>

      <div className="space-y-16">
        <section id="upload-zone" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Upload Zone</h2>
            <p className="mt-1 text-sm text-secondary">
              Single-file drop zone. Pick a file, then switch the file card state.
            </p>
          </div>
          <div className="flex gap-2">
            {UPLOAD_ZONE_STATUSES.map((status) => (
              <Button
                key={status}
                size="sm"
                variant={zoneStatus === status ? "default" : "outline"}
                onClick={() => setZoneStatus(status)}
              >
                {status}
              </Button>
            ))}
          </div>
          <div className="max-w-md">
            <UploadZone
              label="Death certificate"
              labelTooltip="A certified copy from the county works best"
              file={zoneFile}
              onFileSelect={setZoneFile}
              onFileRemove={() => setZoneFile(null)}
              fileCardStatus={zoneStatus}
              fileCardProgress={60}
              fileCardErrorMessage="Upload failed. Try again."
            />
          </div>
        </section>

        <section id="file-card" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">File Card</h2>
            <p className="mt-1 text-sm text-secondary">Uploaded file row in each state.</p>
          </div>
          <div className="grid max-w-md gap-3">
            <FileCard
              fileName="will.pdf"
              fileSize={SAMPLE_FILE_SIZE}
              status="loading"
              progress={45}
            />
            <FileCard
              fileName="will.pdf"
              fileSize={SAMPLE_FILE_SIZE}
              status="complete"
              onRemove={() => logEvent("Remove clicked")}
              onClick={() => logEvent("File card clicked")}
            />
            <FileCard
              fileName="will.pdf"
              fileSize={SAMPLE_FILE_SIZE}
              status="error"
              errorMessage="This file is too large"
              onRemove={() => logEvent("Remove clicked")}
            />
            <FileCard fileName="will.pdf" fileSize={SAMPLE_FILE_SIZE} isRemoving />
          </div>
        </section>

        <section id="document-upload-input" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Document Upload Input</h2>
            <p className="mt-1 text-sm text-secondary">
              Multi-file drop area, enabled and disabled.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <DocumentUploadInput
              onChange={(event) => logEvent(`${event.target.files?.length ?? 0} file(s) selected`)}
            />
            <DocumentUploadInput onChange={() => undefined} disabled />
          </div>
        </section>

        <section id="upload-method-cards" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Upload Method Cards</h2>
            <p className="mt-1 text-sm text-secondary">
              Computer or phone choice, stacked and side by side.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <UploadMethodCards
              variant="stacked"
              hasFile={!!methodFile}
              onFileSelected={(file) => {
                setMethodFile(file);
                logEvent(`Selected ${file.name}`);
              }}
              onSelectPhone={() => setOpenOverlay("mobile")}
            />
            <UploadMethodCards
              variant="side-by-side"
              hasFile={!!methodFile}
              onFileSelected={(file) => {
                setMethodFile(file);
                logEvent(`Selected ${file.name}`);
              }}
              onSelectPhone={() => setOpenOverlay("mobile")}
            />
          </div>
        </section>

        <section id="uploaded-document" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Uploaded Document</h2>
            <p className="mt-1 text-sm text-secondary">Staged file with a document type picker.</p>
          </div>
          <div className="grid max-w-md gap-3">
            <UploadedDocument
              ix={0}
              document={{ name: "death-certificate.pdf" }}
              documentTypes={SAMPLE_DOCUMENT_TYPES}
              documentType={uploadedDocType}
              onDocumentTypeChange={setUploadedDocType}
              onDocumentRemove={() => logEvent("Remove clicked")}
              isErrored={false}
            />
            <UploadedDocument
              ix={1}
              document={{ name: "scan-0042.jpg" }}
              documentTypes={SAMPLE_DOCUMENT_TYPES}
              documentType={DocumentType.WILL}
              onDocumentTypeChange={() => undefined}
              onDocumentRemove={() => logEvent("Remove clicked")}
              isErrored
            />
          </div>
        </section>

        <section id="qr-upload-instructions" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">QR Upload Instructions</h2>
            <p className="mt-1 text-sm text-secondary">Phone upload steps with an upload count.</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setQrCount((count) => count + 1)}>
              Simulate upload
            </Button>
            <Button size="sm" variant="outline" onClick={() => setQrCount(0)}>
              Reset
            </Button>
          </div>
          <div className="max-w-md">
            <QrUploadInstructions uploadCount={qrCount} />
          </div>
        </section>

        <section id="contact-profile-picture" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Contact Profile Picture</h2>
            <p className="mt-1 text-sm text-secondary">Click to pick a photo. Previews locally.</p>
          </div>
          <ContactProfilePicture
            profilePictureUrl={profileUrl}
            isLoadingPhoto={false}
            fullName="Jane Doe"
            fileInputRef={profileInputRef}
            onProfilePictureClick={() => profileInputRef.current?.click()}
            onProfilePictureSelect={(event) => {
              const file = event.target.files?.[0];
              setProfileUrl(file ? URL.createObjectURL(file) : null);
            }}
          />
        </section>

        <section id="upload-documents-form" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Upload Documents Form</h2>
            <p className="mt-1 text-sm text-secondary">Inline multi-document form.</p>
          </div>
          <div className="max-w-xl">
            <UploadDocumentsForm
              multiple
              initiallyShowButtons
              documentTypes={SAMPLE_DOCUMENT_TYPES}
              onCancel={() => logEvent("Cancel clicked")}
              onSuccess={(docs) => logEvent(`${docs?.length ?? 0} document(s) uploaded`)}
            />
          </div>
        </section>

        <section id="upload-document-modal" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Upload Document Modal</h2>
            <p className="mt-1 text-sm text-secondary">Drag-and-drop modal with a 3-file limit.</p>
          </div>
          <Button variant="outline" onClick={() => setOpenOverlay("upload-modal")}>
            Show UploadDocumentModal
          </Button>
          {estateId && (
            <UploadDocumentModal
              isOpen={openOverlay === "upload-modal"}
              onClose={closeOverlay}
              estateId={estateId}
              maxFiles={3}
              onSuccess={(ids) => logEvent(`${ids.length} document(s) uploaded`)}
              onLimitExceeded={() => setOpenOverlay("limit")}
              onTakePhoto={() => setOpenOverlay("mobile")}
            />
          )}
        </section>

        <section id="upload-documents-dialog" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Upload Documents Dialog</h2>
            <p className="mt-1 text-sm text-secondary">Document viewer upload dialog.</p>
          </div>
          <Button variant="outline" onClick={() => setOpenOverlay("documents-dialog")}>
            Show UploadDocumentsDialog
          </Button>
          <UploadDocumentsDialog
            isOpen={openOverlay === "documents-dialog"}
            onClose={closeOverlay}
            onComplete={(docs) => logEvent(`${docs?.length ?? 0} document(s) uploaded`)}
            documentTypes={SAMPLE_DOCUMENT_TYPES}
          />
        </section>

        <section id="mobile-upload-modal" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Mobile Upload Modal</h2>
            <p className="mt-1 text-sm text-secondary">QR code upload with realtime tracking.</p>
          </div>
          <Button variant="outline" onClick={() => setOpenOverlay("mobile")}>
            Show MobileUploadModal
          </Button>
          <MobileUploadModal
            isOpen={openOverlay === "mobile"}
            onClose={closeOverlay}
            onSuccess={() => logEvent("Mobile upload received")}
            onTryAnotherWay={() => setOpenOverlay("upload-modal")}
          />
        </section>

        <section id="upload-limit-modal" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Upload Limit Modal</h2>
            <p className="mt-1 text-sm text-secondary">
              Shown when a free user picks more files than their limit allows.
            </p>
          </div>
          <Button variant="outline" onClick={() => setOpenOverlay("limit")}>
            Show UploadLimitModal
          </Button>
          <UploadLimitModal
            isOpen={openOverlay === "limit"}
            onClose={closeOverlay}
            accepted={sampleFiles.slice(0, 1)}
            blocked={sampleFiles.slice(1)}
            onUpgradeAll={() => logEvent("Upgrade clicked")}
            onUploadAccepted={(files) => {
              logEvent(`Uploading ${files.length} file(s)`);
              closeOverlay();
            }}
          />
        </section>

        <section id="lawyer-upload-dialog" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Lawyer Upload Dialog</h2>
            <p className="mt-1 text-sm text-secondary">
              Attorney upload on the client detail page.
            </p>
          </div>
          <Button variant="outline" onClick={() => setOpenOverlay("lawyer")}>
            Show LawyerUploadDialog
          </Button>
          {estateId && (
            <LawyerUploadDialog
              isOpen={openOverlay === "lawyer"}
              onClose={closeOverlay}
              onComplete={() => logEvent("Lawyer upload complete")}
              estateId={estateId}
            />
          )}
        </section>

        <section id="death-certificate-step" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Death Certificate Step</h2>
            <p className="mt-1 text-sm text-secondary">Home triage modal upload step.</p>
          </div>
          <div className="max-w-xl rounded-xl border border-primary p-6">
            <TriageModalDeathCertificateStep
              personFirstName="John"
              completedUploads={completedUploads}
              onUploadCompleted={(upload) => setCompletedUploads((prev) => [...prev, upload])}
              onUploadRemoved={(fileName) =>
                setCompletedUploads((prev) => prev.filter((u) => u.fileName !== fileName))
              }
              onBack={() => logEvent("Back clicked")}
              onContinue={(hasUploads) => logEvent(`Continue (hasUploads: ${hasUploads})`)}
              onPhoneUpload={() => setOpenOverlay("mobile")}
              onRequestUpload={() => logEvent("Request upload clicked")}
            />
          </div>
        </section>

        <section id="will-upload-step" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Will Upload Step</h2>
            <p className="mt-1 text-sm text-secondary">Legal authority modal upload step.</p>
          </div>
          <div className="max-w-xl rounded-xl border border-primary p-6">
            <LegalAuthorityWillUploadStep
              personFirstName="John"
              onBack={() => logEvent("Back clicked")}
              onContinue={() => logEvent("Continue clicked")}
              onPhoneUpload={() => setOpenOverlay("mobile")}
              onRequestUpload={() => logEvent("Request upload clicked")}
            />
          </div>
        </section>

        <section id="upload-id" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Upload ID (onboarding)</h2>
            <p className="mt-1 text-sm text-secondary">Onboarding flow screen for a photo ID.</p>
          </div>
          <div className="max-w-xl rounded-xl border border-primary p-6">
            <UploadId {...flowProps} />
          </div>
        </section>

        <section id="tax-return-upload" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-primary pb-3">
            <h2 className="font-sans text-lg font-semibold">Tax Return Upload</h2>
            <p className="mt-1 text-sm text-secondary">Financial accounts module flow screen.</p>
          </div>
          <div className="max-w-xl rounded-xl border border-primary p-6">
            <TaxReturnUpload {...flowProps} />
          </div>
        </section>
      </div>
    </div>
  );
};
